import { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { formatISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@components/Drawer';
import { ModalBase } from '@components/Modal';
import { accountActions, alertActions, authActions } from '@actions';
import { companyService, menusService } from '@services';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { ChatType, LocatedInRelationType, paymentMethods, PaymentTypes, LocationType } from '@constants/companies';
import { handleApiError } from '@helpers/api';
import { additionalPhonesForSubmit, locatedInForSubmit, specialHoursForSubmit } from '@helpers/companies';
import { isObject } from '@helpers/isObject';
import { preparePhoneNumberForSubmit } from '@helpers/phone';
import FooterScore from '@features/locations/optimizationScore/FooterScore';
import { LiveScoreProvider } from '@features/locations/optimizationScore/LiveScoreProvider';
import { ScoreAssistantProvider } from '@features/locations/optimizationScore/ScoreAssistantProvider';
import ScoreSummary from '@features/locations/optimizationScore/ScoreSummary';
import ScoreWidget, { ScoreRecommendations } from '@features/locations/optimizationScore/ScoreWidget';
import { getCompanyFormSchema, mapCompanyToFormValues } from './formConfig';
import FormSections from './FormSections';
import useFormSections from './useFormSections';
import SkeletonBody from './SkeletonBody';

const SCORE_PANEL_QUERY = '(min-width: 1280px)';
const SCORE_PANEL_MEDIA = `@media ${SCORE_PANEL_QUERY}`;

const Modal = styled(ModalBase)({
    [SCORE_PANEL_MEDIA]: {
        '& .modal-base-paper': {
            width: 1090,
            maxWidth: 'calc(100% - 32px)',
            height: 'calc(var(--100vh) - 32px)',
        },
        '& .modal-base-content': {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 360px',
            overflow: 'hidden',
        },
    },
});

const FormColumn = styled('div')({
    [SCORE_PANEL_MEDIA]: {
        minHeight: 0,
        overflowY: 'auto',
    },
});

const ScoreDrawer = styled(Drawer)({
    '& .drawer-content': {
        padding: 8,
    },
});

const ScorePanel = styled('aside')({
    minHeight: 0,
    overflowY: 'auto',
    padding: '24px 16px',
    backgroundColor: '#f2f2f2',
    borderLeft: '1px solid var(--divider)',
});

const DEFAULT_FORM_VALUES = {
    locationName: '',
    internalName: '',
    status: '',
    brand: '',
    phoneNumber: '',
    phoneExtension: '',
    additionalPhones: [],
    email: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    country: '',
    postalCode: '',
    hasBusinessAddress: false,
    latitude: '',
    longitude: '',
    locatedInEnabled: false,
    locatedIn: null,
    locatedInRelationType: LocatedInRelationType.Independent,
    shortDescription: '',
    mediumDescription: '',
    longDescription: '',
    openingDate: null,
    workingHoursEnabled: false,
    workingHours: [],
    specialHours: [],
    logoUrl: null,
    coverUrl: null,
    chatLink: '',
    chatLinkAdded: false,
    chatType: ChatType.SMS,
    nationalHealthProviderId: '',
    storeCode: '',
    paymentMethods: paymentMethods.map(item => ({ name: item.name, value: false })),
    services: [{ name: '' }],
    certifications: [{ name: '' }],
    primaryCategory: [],
    secondaryCategories: [],
    socialLinks: {
        facebook: '',
        instagram: '',
        x: '',
        linkedIn: '',
        pinterest: '',
        youTube: '',
        tikTok: '',
    },
    type: LocationType.Location,
    geoModifier: '',
    featuredMessage: '',
    featuredMessageUrl: '',
    featuredMessageEnabled: false,
    firstName: '',
    lastName: '',
    degreeId: '',
    office: '',
    headshotUrl: '',
    menuIds: [],
};

const MENU_LOCATION_TYPES = [LocationType.Hotel, LocationType.Restaurant];

const sameIds = (a, b) => {
    const left = [...(a || [])].sort();
    const right = [...(b || [])].sort();
    return left.length === right.length && left.every((id, index) => id === right[index]);
};

const EditForm = ({ isOpen, onClose, selectedCompany, setSelectedCompany, account, loading = false }) => {
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { data: countries, loading: countriesLoading } = useSelector(state => state.countries);
    const dispatch = useDispatch();
    const isInitialFormValid = useRef(false);

    const resolver = useYupValidationResolver(getCompanyFormSchema(account, selectedCompany));

    const form = useForm({
        mode: 'all',
        resolver,
        defaultValues: DEFAULT_FORM_VALUES,
    });
    const {
        getValues,
        handleSubmit,
        watch,
        trigger,
        reset,
        resetField,
        formState: { isValid, isDirty, isSubmitting, dirtyFields },
    } = form;

    const [menus, setMenus] = useState([]);
    const initialMenuIds = useRef([]);

    const hasScorePanel = useMediaQuery(SCORE_PANEL_QUERY, { noSsr: true });
    const [isScoreDrawerOpen, setIsScoreDrawerOpen] = useState(false);
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const sessionKey = isOpen ? (selectedCompany?.companyId ?? null) : null;
    const { sectionsOpen, toggleSection, revealField } = useFormSections(sessionKey);

    useEffect(() => {
        setIsScoreDrawerOpen(false);
    }, [hasScorePanel, sessionKey]);

    const revealFromScore = target => {
        setIsScoreDrawerOpen(false);
        revealField(target);
    };

    useEffect(() => {
        if (selectedCompany?.companyId && !countriesLoading) {
            reset(mapCompanyToFormValues(selectedCompany, account, countries, language));
            trigger().then(valid => {
                isInitialFormValid.current = valid;
            });
        } else if (!selectedCompany?.companyId) {
            reset(DEFAULT_FORM_VALUES);
            isInitialFormValid.current = false;
        }
    }, [selectedCompany, countriesLoading]);

    useEffect(() => {
        const companyId = selectedCompany?.companyId;
        if (!companyId) {
            setMenus([]);
            initialMenuIds.current = [];
            return undefined;
        }

        let cancelled = false;
        menusService
            .getAllUnpaged()
            .then(result => {
                if (cancelled) return;
                const items = result.items || [];
                setMenus(items);
                const assignedIds = items
                    .filter(menu => (menu.locationIds || []).includes(companyId))
                    .map(menu => menu.id);
                initialMenuIds.current = assignedIds;
                resetField('menuIds', { defaultValue: assignedIds });
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [selectedCompany?.companyId, resetField]);

    const onSubmit = async () => {
        const { menuIds, ...values } = getValues();
        const chatLink = preparePhoneNumberForSubmit(values.chatLink, true);
        let updatedCompany;
        try {
            updatedCompany = await companyService.updateCompany({
                ...selectedCompany,
                ...values,
                brand: values.brand ? { id: values.brand } : null,
                socialLinks: Object.fromEntries(
                    Object.entries(values.socialLinks || {}).map(([key, url]) => [key, url?.trim() || null]),
                ),
                phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber),
                additionalPhones: additionalPhonesForSubmit(values, dirtyFields, selectedCompany?.additionalPhones),
                openingDate: values.openingDate ? formatISO(values.openingDate, { representation: 'date' }) : null,
                chatLink: chatLink || null,
                chatType: values.chatLink?.length > 3 ? values.chatType : null,
                locatedIn: locatedInForSubmit(values, dirtyFields),
                categories: [
                    { ...values.primaryCategory[0], isPrimary: true },
                    ...values.secondaryCategories
                        .filter(item => isObject(item.category[0]))
                        .map(item => ({ ...item.category[0], isPrimary: false })),
                ],
                services: values.services.map(s => s.name).filter(Boolean),
                certifications: values.certifications.map(c => c.name).filter(Boolean),
                paymentMethods: values.paymentMethods.filter(item => item.value).map(item => PaymentTypes[item.name]),
                specialHours: specialHoursForSubmit(values.specialHours),
            });
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
            return;
        }

        // Location is saved. Menu assignment is a separate request, so report its failure
        // distinctly instead of surfacing it as a location-update error, and keep the
        // modal open so the assignment can be retried without re-saving the location.
        const menusChanged = MENU_LOCATION_TYPES.includes(values.type) && !sameIds(menuIds, initialMenuIds.current);
        if (menusChanged) {
            try {
                await menusService.setLocationMenus(selectedCompany.companyId, menuIds || []);
                initialMenuIds.current = menuIds || [];
            } catch (menuError) {
                setSelectedCompany(updatedCompany);
                dispatch(accountActions.get());
                dispatch(authActions.getCurrentUser());
                handleApiError(menuError, 'SettingsCompanies.menuAssignmentFailed');
                return;
            }
        }

        setSelectedCompany(updatedCompany);
        dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        dispatch(accountActions.get());
        dispatch(authActions.getCurrentUser());
        reset({ ...values, menuIds });
        onClose();
    };

    const title = watch('internalName') || selectedCompany?.internalName || '';

    return (
        <FormProvider {...form}>
            <LiveScoreProvider selectedCompany={selectedCompany}>
                <ScoreAssistantProvider
                    companyId={selectedCompany?.companyId}
                    sessionKey={sessionKey}
                    onReveal={revealFromScore}
                    onGeneratingChange={setIsAiGenerating}
                >
                    <Modal
                        fullScreen
                        isOpen={isOpen}
                        handleClose={onClose}
                        isDirty={isDirty}
                        title={title}
                        onPrimaryAction={handleSubmit(onSubmit)}
                        primaryActionText={t('buttons.save')}
                        primaryActionDisabled={loading || isSubmitting || isAiGenerating || !isValid || !isDirty}
                        primaryActionTooltip={isAiGenerating ? t('ScorePanel.waitForAi') : ''}
                        footerLeft={
                            loading || hasScorePanel ? null : <FooterScore onClick={() => setIsScoreDrawerOpen(true)} />
                        }
                    >
                        <FormColumn>
                            {loading ? (
                                <SkeletonBody />
                            ) : (
                                <FormSections
                                    account={account}
                                    language={language}
                                    countries={countries}
                                    selectedCompany={selectedCompany}
                                    setSelectedCompany={setSelectedCompany}
                                    isInitialFormValid={isInitialFormValid.current}
                                    menus={menus}
                                    sectionsOpen={sectionsOpen}
                                    toggleSection={toggleSection}
                                />
                            )}
                        </FormColumn>
                        {hasScorePanel && <ScorePanel>{!loading && <ScoreWidget />}</ScorePanel>}
                    </Modal>
                    <ScoreDrawer
                        anchor="right"
                        open={isOpen && isScoreDrawerOpen && !hasScorePanel}
                        onClose={() => setIsScoreDrawerOpen(false)}
                        title={<ScoreSummary />}
                        aboveModal
                    >
                        <ScoreRecommendations />
                    </ScoreDrawer>
                </ScoreAssistantProvider>
            </LiveScoreProvider>
        </FormProvider>
    );
};

export default EditForm;
